import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "@/utils/axiosInstance";
import { handleThunkError } from "@/utils/thunkErrorHandler";
import {
  Params,
  CreateServiceData,
  UpdateServiceData,
  ServiceSlug,
} from "@/types";

const endpoint = {
  services: "/services",
};

export const fetchServices = createAsyncThunk(
  "services/fetchServices",
  async (params: Params, thunkAPI) => {
    try {
      const response = await axiosInstance.get(endpoint.services, { params });
      return response.data;
    } catch (error) {
      return handleThunkError(
        error,
        thunkAPI,
        "Erro ao carregar a lista de serviços",
      );
    }
  },
);

export const fetchServicesAll = createAsyncThunk(
  "services/fetchServicesAll",
  async (
    params: Omit<Params, "current_page" | "per_page"> & {
      without_pagination: 1;
    },
    thunkAPI,
  ) => {
    try {
      const response = await axiosInstance.get(endpoint.services, {
        params,
      });
      return response.data;
    } catch (error) {
      return handleThunkError(
        error,
        thunkAPI,
        "Erro ao carregar todos os serviços",
      );
    }
  },
);

export const createService = createAsyncThunk(
  "services/createService",
  async (serviceData: any, thunkAPI) => {
    try {
      const isFormData =
        typeof FormData !== "undefined" && serviceData instanceof FormData;
      const response = await axiosInstance.post(
        endpoint.services,
        serviceData,
        {
          headers: isFormData
            ? { "Content-Type": "multipart/form-data" }
            : undefined,
        },
      );
      return response.data;
    } catch (error) {
      return handleThunkError(
        error,
        thunkAPI,
        "Não foi possível cadastrar o serviço",
      );
    }
  },
);

export const updateService = createAsyncThunk(
  "services/updateService",
  async (data: any, thunkAPI) => {
    try {
      const { slug, ...rest } = data;
      const payload = (rest as any).data ? (rest as any).data : rest;
      const isFormData =
        typeof FormData !== "undefined" && payload instanceof FormData;
      const response = await axiosInstance.post(
        `${endpoint.services}/${slug}?_method=PUT`,
        payload,
        {
          headers: isFormData
            ? { "Content-Type": "multipart/form-data" }
            : undefined,
        },
      );
      return response.data;
    } catch (error) {
      return handleThunkError(
        error,
        thunkAPI,
        "Não foi possível atualizar o serviço",
      );
    }
  },
);

// Upload only image for existing service
export const uploadServiceImage = createAsyncThunk(
  "services/uploadServiceImage",
  async ({ slug, image }: { slug: string; image: File }, thunkAPI) => {
    try {
      const formData = new FormData();
      formData.append("image", image);
      const response = await axiosInstance.post(
        `${endpoint.services}/${slug}/upload-image`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      return response.data;
    } catch (error) {
      return handleThunkError(
        error,
        thunkAPI,
        "Não foi possível enviar a imagem do serviço",
      );
    }
  },
);

export const deleteService = createAsyncThunk(
  "services/deleteService",
  async (slug: ServiceSlug, thunkAPI) => {
    try {
      const response = await axiosInstance.delete(
        `${endpoint.services}/${slug}`,
      );
      return response.data;
    } catch (error) {
      return handleThunkError(
        error,
        thunkAPI,
        "Não foi possível excluir o serviço",
      );
    }
  },
);

export const fetchServiceByClient = createAsyncThunk(
  "services/fetchServiceByClient",
  async (
    data: { client: number | string | null; startDateTime?: string },
    thunkAPI,
  ) => {
    try {
      const response = await axiosInstance.get(
        `${endpoint.services}/client-services`,
        {
          params: {
            client_id: data?.client,
            start_date_time: data?.startDateTime,
          },
        },
      );
      return response.data;
    } catch (error) {
      return handleThunkError(
        error,
        thunkAPI,
        "Erro ao carregar serviços do cliente",
      );
    }
  },
);
